import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { header } from '../../components/header.encry';
import Modal from '../../components/Modal';
import { toast } from 'react-toastify';
import StoresHelper from '../../components/storesHelper';
import { getDolibarrToken } from '../../utils/authStorage';

const PERMISSIONS = [
    { value: 'admin', label: 'Administrateur' },
    { value: 'support', label: 'Support messagerie' },
    { value: 'manager', label: 'Gestionnaire' },
    { value: 'accountant', label: 'Comptable' },
    { value: 'operator', label: 'Opérateur' },
    { value: 'driver', label: 'Chauffeur' },
    { value: 'carrefour', label: 'Carrefour' },
    { value: 'public', label: 'Commercial Public' },
];

const normalizeId = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
        if (value.$oid) return value.$oid;
        if (value._id) return value._id;
        if (value.id) return value.id;
    }
    return String(value);
};

function UsersView() {
    const [users, setUsers] = useState([]);
    const [categories, setCategories] = useState([]);
    const [warehouses, setWareHouses] = useState([]);
    const [stores, setStores] = useState([]);

    const emptyForm = {
        username: '',
        name: '',
        lastname: '',
        current_email: '',
        token: '',
        phone: '',
        access_permissions: [],
        default_storage_tag: '',
        default_warehouse_id: '',
        default_store_id: '',
        affected_store_ids: [],
        parentUser: '',
        data: { hikvision: { employee_id: '' } },
    };

    const [formData, setFormData] = useState(emptyForm);
    const [editingUserId, setEditingUserId] = useState(null);
    const [passwordUserId, setPasswordUserId] = useState(null);
    const [passwordForm, setPasswordForm] = useState({ password: '' });
    const [showPassword, setShowPassword] = useState(false);

    const isAdmin = () => {
        const role = sessionStorage.getItem('roles');
        return role && JSON.parse(role).includes('admin');
    };

    useEffect(() => {
        if (isAdmin()) fetchUsers();
        (async function () {
            const store_tmp = await StoresHelper();
            setStores(store_tmp);
        })();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (formData.access_permissions.includes('carrefour')) {
            fetchCategoriesAndWarehouses();
        }
        // eslint-disable-next-line
    }, [formData.access_permissions]);

    const fetchUsers = async () => {
        const res = await axios.get(process.env?.REACT_APP_PEGASUS_API_URL + '/users', header());
        setUsers(res.data);
    };

    const getParentName = (parentId) => {
        const normalizedId = normalizeId(parentId);
        if (!normalizedId) return '';
        const parent = users.find(u => String(u._id) === String(normalizedId));
        return parent ? `${parent.name} ${parent.lastname}` : '';
    };

    const fetchCategoriesAndWarehouses = async () => {
        const token = await getDolibarrToken();
        if (!token) {
            toast.error('Token Dolibarr manquant. Merci de vous reconnecter.');
            return;
        }
        try {
            const [catRes, whRes] = await Promise.all([
                axios.get('https://compta.le-celadon.ma/api/index.php/categories?sortfield=t.rowid&sortorder=ASC&limit=100', {
                    headers: { DOLAPIKEY: token, 'Content-Type': 'application/json' }
                }),
                axios.get('https://compta.le-celadon.ma/api/index.php/warehouses', {
                    headers: { DOLAPIKEY: token, 'Content-Type': 'application/json' }
                })
            ]);
            setCategories(catRes.data);
            setWareHouses(whRes.data);
        } catch (err) {
            toast.error('Erreur lors du chargement des catégories ou entrepôts');
        }
    };

    // Get possible store choices depending on carrefour permission
    const getStoreOptions = (perms = formData.access_permissions) => {
        if (!Array.isArray(stores)) return [];
        if (perms.includes('carrefour')) {
            return stores.filter(s => s.type === 'carrefour');
        }
        return stores;
    };

    // For display: get store label from id (works for all users)
    const getStoreName = (storeId) => {
        if (!storeId) return '';
        const store = stores.find(s =>
            String(s.id || s._id) === String(storeId)
        );
        return store ? (store.label || store.name) : '';
    };

    const getAffectedStoreNames = (storeIds = []) => {
        const safeStoreIds = Array.isArray(storeIds) ? storeIds : [];
        return safeStoreIds
            .map((storeId) => getStoreName(storeId))
            .filter(Boolean)
            .join(', ');
    };

    const handleChange = (e) => {
        if (e.target.name === 'hikvision_employee_id') {
            setFormData(prev => ({
                ...prev,
                data: {
                    ...(prev.data || {}),
                    hikvision: {
                        ...((prev.data && prev.data.hikvision) || {}),
                        employee_id: e.target.value
                    }
                }
            }));
            return;
        }
        if (e.target.name === 'access_permissions') {
            const { value, checked } = e.target;
            setFormData(prev => {
                let permissions = [...prev.access_permissions];
                if (checked) {
                    permissions.push(value);
                } else {
                    permissions = permissions.filter(permission => permission !== value);
                }
                // Si on décoche carrefour, reset les champs liés
                return {
                    ...prev,
                    access_permissions: permissions,
                    default_storage_tag: permissions.includes('carrefour') ? prev.default_storage_tag : '',
                    default_warehouse_id: permissions.includes('carrefour') ? prev.default_warehouse_id : '',
                    default_store_id: permissions.includes('carrefour')
                        ? (getStoreOptions(permissions).some(s => s.id === prev.default_store_id || s._id === prev.default_store_id)
                            ? prev.default_store_id
                            : '')
                        : prev.default_store_id
                };
            });
        } else if (e.target.name === 'affected_store_ids') {
            const selectedValues = Array.from(e.target.selectedOptions || []).map((option) => option.value);
            setFormData(prev => ({ ...prev, affected_store_ids: selectedValues }));
        } else {
            setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const payload = { ...formData };
        if (!formData.access_permissions.includes('carrefour')) {
            delete payload.default_storage_tag;
            delete payload.default_warehouse_id;
        } else {
            payload.default_storage_tag = String(formData.default_storage_tag || '');
            payload.default_warehouse_id = String(formData.default_warehouse_id || '');
        }

        try {
            if (editingUserId) {
                await axios.put(process.env?.REACT_APP_PEGASUS_API_URL + `/users/${editingUserId}`, payload, header());
            } else {
                await axios.post(process.env?.REACT_APP_PEGASUS_API_URL + '/users', payload, header());
            }
            setFormData(emptyForm);
            setEditingUserId(null);
            fetchUsers();
        } catch (err) {
            alert('Erreur lors de la sauvegarde');
        }
    };

    const handleEdit = (user) => {
        setEditingUserId(user._id);
        setFormData({
            username: user.username,
            name: user.name,
            lastname: user.lastname,
            current_email: user.current_email || '',
            token: user.token,
            phone: user.phone || '',
            access_permissions: user.access_permissions,
            default_storage_tag: user.default_storage_tag || '',
            default_warehouse_id: user.default_warehouse_id || '',
            default_store_id: user.default_store_id || '',
            affected_store_ids: Array.isArray(user.affected_store_ids) ? user.affected_store_ids.map(normalizeId).filter(Boolean) : [],
            parentUser: normalizeId(user.parentUser) || '',
            data: { hikvision: { employee_id: (user.data && user.data.hikvision && user.data.hikvision.employee_id) || '' } }
        });
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        if (!passwordUserId) {
            return;
        }

        try {
            await axios.patch(
                `${process.env?.REACT_APP_PEGASUS_API_URL}/users/${passwordUserId}/password`,
                { password: passwordForm.password },
                header()
            );
            setPasswordForm({ password: '' });
            setPasswordUserId(null);
            toast.success('Mot de passe mis à jour');
        } catch (err) {
            toast.error('Erreur lors de la mise à jour du mot de passe');
        }
    };

    const generateStrongPassword = () => {
        const length = 16;
        const charset = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*';
        let generated = '';
        const randomValues = new Uint32Array(length);
        window.crypto.getRandomValues(randomValues);
        for (let i = 0; i < length; i += 1) {
            generated += charset[randomValues[i] % charset.length];
        }
        setPasswordForm({ password: generated });
        setShowPassword(true);
    };

    const getPermissionLabels = (user) => (
        (Array.isArray(user.access_permissions) ? user.access_permissions : [])
            .map(code => PERMISSIONS.find(p => p.value === code)?.label || code)
            .join(', ')
    );

    const getCarrefourCategoryLabel = (user) => (
        Array.isArray(user.access_permissions) && user.access_permissions.includes('carrefour') && user.default_storage_tag
            ? categories.find(c => String(c.id) === String(user.default_storage_tag))?.label || user.default_storage_tag
            : ''
    );

    const getCarrefourWarehouseLabel = (user) => (
        Array.isArray(user.access_permissions) && user.access_permissions.includes('carrefour') && user.default_warehouse_id
            ? warehouses.find(w => String(w.id) === String(user.default_warehouse_id))?.label || user.default_warehouse_id
            : ''
    );

    const openPasswordModal = (user) => {
        setPasswordUserId(user._id);
        setPasswordForm({ password: '' });
        setShowPassword(false);
    };

    const renderUserActions = (user, compact = false) => (
        <div className={`d-flex ${compact ? 'flex-column' : 'flex-wrap'} gap-2`}>
            <button
                className="btn btn-sm btn-warning"
                data-bs-toggle="modal"
                data-bs-target="#userHandle"
                onClick={() => handleEdit(user)}
            >
                Modifier
            </button>
            <button
                className="btn btn-sm btn-secondary"
                data-bs-toggle="modal"
                data-bs-target="#userPassword"
                onClick={() => openPasswordModal(user)}
            >
                {compact ? 'Mot de passe' : 'Changer mot de passe'}
            </button>
        </div>
    );

    if (!isAdmin()) return null;

    return (
        <div className="container-fluid px-2 px-md-4 mt-4">
            <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mb-4">
                <div>
                    <h2 className="mb-1">Gestion des utilisateurs</h2>
                    <div className="text-muted">{users.length} utilisateur(s)</div>
                </div>
                <div className="text-md-end">
                    <button
                        className="btn btn-success w-100 w-md-auto"
                        data-bs-toggle="modal"
                        data-bs-target="#userHandle"
                        onClick={() => {
                            setFormData(emptyForm);
                            setEditingUserId(null)
                        }}
                    >
                        Ajouter un utilisateur
                    </button>
                </div>
            </div>

            <h4 className="mb-3">Liste des utilisateurs</h4>
            <div className="d-none d-lg-block table-responsive">
                <table className="table table-striped table-bordered table-hover align-middle">
                    <thead>
                        <tr>
                            <th>Nom d'utilisateur</th>
                            <th>Prénom</th>
                            <th>Nom</th>
                            <th>Email actuel</th>
                            <th>Téléphone</th>
                            <th>Parent</th>
                            <th>Permissions</th>
                            <th>Catégorie</th>
                            <th>Entrepôt</th>
                            <th>Magasin assigné</th>
                            <th>Magasins affectes</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map(u => (
                            <tr key={u._id}>
                                <td>{u.username}</td>
                                <td>{u.name}</td>
                                <td>{u.lastname}</td>
                                <td>{u.current_email || '-'}</td>
                                <td>{u.phone}</td>
                                <td>{getParentName(u.parentUser)}</td>
                                <td>{getPermissionLabels(u)}</td>
                                <td>{getCarrefourCategoryLabel(u)}</td>
                                <td>{getCarrefourWarehouseLabel(u)}</td>
                                <td>
                                    {getStoreName(u.default_store_id)}
                                </td>
                                <td>
                                    {getAffectedStoreNames(u.affected_store_ids)}
                                </td>
                                <td>{renderUserActions(u)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="d-lg-none">
                {users.map(u => (
                    <div className="card shadow-sm mb-3" key={u._id}>
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-start gap-3">
                                <div className="min-w-0">
                                    <h5 className="mb-1 text-break">{u.username}</h5>
                                    <div className="fw-semibold text-break">{u.name} {u.lastname}</div>
                                    {u.current_email && <div className="text-muted small text-break">{u.current_email}</div>}
                                    {u.phone && <div className="text-muted small text-break">{u.phone}</div>}
                                </div>
                                <div className="flex-shrink-0">
                                    {renderUserActions(u, true)}
                                </div>
                            </div>
                            <div className="border-top mt-3 pt-3">
                                <div className="small text-muted">Permissions</div>
                                <div className="text-break">{getPermissionLabels(u) || '-'}</div>
                            </div>
                            <div className="row g-3 mt-1">
                                <div className="col-12 col-sm-6">
                                    <div className="small text-muted">Parent</div>
                                    <div className="text-break">{getParentName(u.parentUser) || '-'}</div>
                                </div>
                                <div className="col-12 col-sm-6">
                                    <div className="small text-muted">Magasin assigné</div>
                                    <div className="text-break">{getStoreName(u.default_store_id) || '-'}</div>
                                </div>
                                <div className="col-12">
                                    <div className="small text-muted">Magasins affectés</div>
                                    <div className="text-break">{getAffectedStoreNames(u.affected_store_ids) || '-'}</div>
                                </div>
                                {getCarrefourCategoryLabel(u) && (
                                    <div className="col-12 col-sm-6">
                                        <div className="small text-muted">Catégorie</div>
                                        <div className="text-break">{getCarrefourCategoryLabel(u)}</div>
                                    </div>
                                )}
                                {getCarrefourWarehouseLabel(u) && (
                                    <div className="col-12 col-sm-6">
                                        <div className="small text-muted">Entrepôt</div>
                                        <div className="text-break">{getCarrefourWarehouseLabel(u)}</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            {/* Modal formulaire utilisateur */}
            <Modal
                modalId="userHandle"
                modalType="modal-lg"
                submitLabel={editingUserId ? "Modifier" : "Ajouter"}
                onSubmit={handleSubmit}
            >
                <form onSubmit={handleSubmit} className="mb-4">
                    <div className="row g-3 mb-3">
                        <div className="col-12 col-md-6">
                            <input
                                name="username"
                                placeholder="Nom d'utilisateur"
                                value={formData.username}
                                onChange={handleChange}
                                className="form-control"
                                required
                            />
                        </div>
                        <div className="col-12 col-md-6">
                            <input
                                name="name"
                                placeholder="Prénom"
                                value={formData.name}
                                onChange={handleChange}
                                className="form-control"
                                required
                            />
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-12">
                            <input
                                name="lastname"
                                placeholder="Nom"
                                value={formData.lastname}
                                onChange={handleChange}
                                className="form-control"
                                required
                            />
                        </div>
                    </div>

                    <div className="mb-3">
                        <input
                            type="email"
                            name="current_email"
                            placeholder="Email actuel"
                            value={formData.current_email}
                            onChange={handleChange}
                            className="form-control"
                        />
                    </div>

                    <div className="mb-3">
                        <input
                            name="phone"
                            placeholder="Téléphone"
                            value={formData.phone}
                            onChange={handleChange}
                            className="form-control"
                        />
                    </div>

                    {isAdmin() && (
                        <div className="mb-3">
                            <label className="form-label">Hikvision Employee ID</label>
                            <input
                                name="hikvision_employee_id"
                                placeholder="Hikvision employee id"
                                value={(formData.data && formData.data.hikvision && formData.data.hikvision.employee_id) || ''}
                                onChange={handleChange}
                                className="form-control"
                            />
                        </div>
                    )}

                    <div className="mb-3">
                        <label className="form-label">Utilisateur parent</label>
                        <select
                            className="form-select"
                            name="parentUser"
                            value={formData.parentUser}
                            onChange={handleChange}
                        >
                            <option value="">Aucun</option>
                            {users.map(u => (
                                <option key={u._id} value={u._id}>
                                    {u.name} {u.lastname}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="mb-3">
                        <label className="form-label">Permissions</label><br />
                        <div className="row g-2">
                        {PERMISSIONS.map((perm) => (
                            <div className="col-12 col-sm-6" key={perm.value}>
                              <div className="form-check">
                                <input
                                    type="checkbox"
                                    name="access_permissions"
                                    value={perm.value}
                                    checked={formData.access_permissions.includes(perm.value)}
                                    onChange={handleChange}
                                    className="form-check-input"
                                    id={"perm_" + perm.value}
                                />
                                <label className="form-check-label" htmlFor={"perm_" + perm.value}>
                                    {perm.label}
                                </label>
                              </div>
                            </div>
                        ))}
                        </div>
                    </div>

                    {/* Champs spécifiques Carrefour */}
                    {formData.access_permissions.includes('carrefour') && (
                        <>
                            <div className="mb-3">
                                <label className="form-label">Catégorie (Tag Carrefour)</label>
                                <select
                                    className="form-select"
                                    name="default_storage_tag"
                                    value={formData.default_storage_tag}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="">Sélectionnez une catégorie</option>
                                    {categories.map(cat => (
                                        <option key={cat.id} value={cat.id}>{cat.label}</option>
                                    ))}
                                </select>

                                <label className="form-label mt-2">Entrepôt Carrefour</label>
                                <select
                                    className="form-select"
                                    name="default_warehouse_id"
                                    value={formData.default_warehouse_id}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="">Sélectionnez un entrepôt</option>
                                    {warehouses.map(wh => (
                                        <option key={wh.id} value={wh.id}>{wh.label}</option>
                                    ))}
                                </select>
                            </div>
                        </>
                    )}

                    {/* Always show store selection */}
                    <div className="mb-3">
                        <label className="form-label">
                            {formData.access_permissions.includes('carrefour')
                                ? 'Magasin Carrefour'
                                : 'Magasin assigné'}
                        </label>
                        <select
                            className="form-select"
                            name="default_store_id"
                            value={formData.default_store_id}
                            onChange={handleChange}
                            required
                        >
                            <option value="">Sélectionnez un magasin</option>
                            {getStoreOptions().map(store => (
                                <option key={store.id || store._id} value={store.id || store._id}>
                                    {store.label || store.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="mb-3">
                        <label className="form-label">Magasins affectes</label>
                        <select
                            className="form-select"
                            name="affected_store_ids"
                            value={formData.affected_store_ids}
                            onChange={handleChange}
                            multiple
                            size={Math.min(8, Math.max(3, getStoreOptions().length))}
                        >
                            {getStoreOptions().map(store => (
                                <option key={store.id || store._id} value={store.id || store._id}>
                                    {store.label || store.name}
                                </option>
                            ))}
                        </select>
                        <div className="form-text">
                            Le magasin assigne reste le magasin par defaut. Cette liste ajoute les autres magasins visibles pour l'utilisateur.
                        </div>
                    </div>
                </form>
            </Modal>
            <Modal
                modalId="userPassword"
                modalType="modal-md"
                submitLabel="Changer"
                onSubmit={handlePasswordSubmit}
                title="Changer le mot de passe"
            >
                <form onSubmit={handlePasswordSubmit} className="mb-4">
                    <div className="mb-3">
                        <label className="form-label">Nouveau mot de passe</label>
                        <div className="d-flex flex-column flex-sm-row gap-2">
                            <input
                                type={showPassword ? "text" : "password"}
                                name="password"
                                value={passwordForm.password}
                                onChange={(e) => setPasswordForm({ password: e.target.value })}
                                className="form-control"
                                required
                            />
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => setShowPassword((prev) => !prev)}
                            >
                                {showPassword ? 'Masquer' : 'Voir'}
                            </button>
                            <button
                                type="button"
                                className="btn btn-outline-primary"
                                onClick={generateStrongPassword}
                            >
                                Générer
                            </button>
                        </div>
                    </div>
                </form>
            </Modal>
        </div>
    );
}

export default UsersView;
